import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { Socket } from 'socket.io-client';
import socketManager from '../services/socketManager';
import { socketService, SocketData, CallEvent } from '../services/unifiedSocketService';
import { useAuth } from '../hooks/useAuth';
import { logger } from '../utils/logger';

export type CallStatus = 'IDLE' | 'RINGING' | 'IN_CALL' | 'ENDED';

interface Call {
  id: string;
  callerId: string;
  receiverId: string;
  type: 'video' | 'audio';
  status?: string;
}

interface SocketContextType {
  socket: Socket | null;
  isConnected: boolean;
  callStatus: CallStatus;
  incomingCall: Call | null;
  emit: (event: string, data?: SocketData) => void;
  on: (event: string, callback: (...args: unknown[]) => void) => void;
  off: (event: string, callback?: (...args: unknown[]) => void) => void;
  joinRoom: (roomId: string) => void;
  leaveRoom: (roomId: string) => void;
  startCall: (receiverId: string, type?: 'video' | 'audio') => void;
  acceptCall: (callId: string) => void;
  rejectCall: (callId: string) => void;
  endCall: (callId: string) => void;
  setCallStatus: (status: CallStatus) => void;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

interface _SocketProviderProps {
  children: React.ReactNode;
}

export const SocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [callStatus, setCallStatus] = useState<CallStatus>('IDLE');
  const [incomingCall, setIncomingCall] = useState<Call | null>(null);
  const { user } = useAuth();
  
  // Utiliser une ref pour suivre si l'initialisation est lancée
  const isInitializingRef = useRef(false);

  const connect = useCallback(async () => {
    if (isInitializingRef.current || socketService.isSocketConnected()) return;
    
    isInitializingRef.current = true;
    try {
      const activeSocket = await socketManager.connect();
      if (activeSocket) {
        setSocket(activeSocket);
        setIsConnected(activeSocket.connected);
        
        // Setup listeners once
        activeSocket.on('connect', () => setIsConnected(true));
        activeSocket.on('disconnect', () => setIsConnected(false));
      }
    } catch (error) {
      logger.error('[SocketContext] Failed to connect:', error);
    } finally {
      isInitializingRef.current = false;
    }
  }, []);

  useEffect(() => {
    // La connexion est déclenchée quand l'utilisateur est disponible
    if (user?.id) {
      connect();
    }
    
    // Pas de watchdog ici. La gestion de la reconnexion est déléguée
    // à socket.io nativement dans le service.
  }, [user?.id, connect]);

  // Socket methods
  const emit = useCallback((event: string, data?: SocketData) => {
    socketService.emit(event, data);
  }, []);

  const on = useCallback((event: string, callback: (...args: unknown[]) => void) => {
    socketService.on(event, callback);
  }, []);

  const off = useCallback((event: string, callback?: (...args: unknown[]) => void) => {
    socketService.off(event, callback);
  }, []);

  const joinRoom = useCallback((roomId: string) => {
    socketService.emit('join:room', { roomId });
  }, []);

  const leaveRoom = useCallback((roomId: string) => {
    socketService.emit('leave:room', { roomId });
  }, []);

  // Call Methods
  const handleCallEvent = useCallback((...args: unknown[]) => {
    const event = args[0] as CallEvent;
    if (!event) return;
    switch (event.status) {
      case 'ringing':
        setIncomingCall({
          id: event.callId,
          callerId: event.callerId || '',
          receiverId: event.receiverId || '',
          type: event.type || 'video',
        });
        setCallStatus('RINGING');
        break;
      case 'accepted':
        setCallStatus('IN_CALL');
        setIncomingCall(null);
        break;
      case 'declined':
        setCallStatus('IDLE');
        setIncomingCall(null);
        break;
      case 'ended':
        setCallStatus('ENDED');
        setIncomingCall(null);
        setTimeout(() => setCallStatus('IDLE'), 2000);
        break;
    }
  }, []);

  const startCall = useCallback((receiverId: string, type: 'video' | 'audio' = 'video') => {
    socketService.startCall(receiverId, type);
    setCallStatus('RINGING');
  }, []);

  const acceptCall = useCallback((callId: string) => {
    socketService.acceptCall(callId);
    setCallStatus('IN_CALL');
    setIncomingCall(null);
  }, []);

  const rejectCall = useCallback((callId: string) => {
    socketService.rejectCall(callId);
    setCallStatus('IDLE');
    setIncomingCall(null);
  }, []);

  const endCall = useCallback((callId: string) => {
    socketService.endCall(callId);
    setCallStatus('ENDED');
    setTimeout(() => setCallStatus('IDLE'), 2000);
  }, []);

  // Effect to attach call listeners when socket is ready
  useEffect(() => {
    if (socket && isConnected) {
      socketService.onCallEvent(handleCallEvent);
      return () => {
        socketService.off('call:event', handleCallEvent);
      };
    }
  }, [socket, isConnected, handleCallEvent]);

  const contextValue: SocketContextType = {
    socket,
    isConnected,
    callStatus,
    incomingCall,
    emit,
    on,
    off,
    joinRoom,
    leaveRoom,
    startCall,
    acceptCall,
    rejectCall,
    endCall,
    setCallStatus,
  };

  return <SocketContext.Provider value={contextValue}>{children}</SocketContext.Provider>;
};

export const useSocket = (): SocketContextType => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

// WebRTC Signaling Hook
export const useWebRTCSignaling = (roomId: string) => {
  const { socket, emit, on, off } = useSocket();
  const [isSignalingReady, setIsSignalingReady] = useState(false);

  useEffect(() => {
    if (!socket || !roomId) return;

    // Join WebRTC signaling room
    emit('webrtc:join', { roomId });

    const handleSignalingReady = () => {
      setIsSignalingReady(true);
    };

    const handleSignalingError = (error: unknown) => {
      logger.error('WebRTC signaling error:', error);
      setIsSignalingReady(false);
    };

    on('webrtc:ready', handleSignalingReady);
    on('webrtc:error', handleSignalingError);

    return () => {
      off('webrtc:ready', handleSignalingReady);
      off('webrtc:error', handleSignalingError);
      emit('webrtc:leave', { roomId });
      setIsSignalingReady(false);
    };
  }, [socket, roomId, emit, on, off]);

  const sendOffer = (offer: RTCSessionDescriptionInit, targetUserId: string | number) => {
    emit('webrtc:offer', { roomId, offer, targetUserId });
  };

  const sendAnswer = (answer: RTCSessionDescriptionInit, targetUserId: string | number) => {
    emit('webrtc:answer', { roomId, answer, targetUserId });
  };

  const sendIceCandidate = (candidate: RTCIceCandidateInit, targetUserId: string | number) => {
    emit('webrtc:ice-candidate', { roomId, candidate, targetUserId });
  };

  return {
    isSignalingReady,
    sendOffer,
    sendAnswer,
    sendIceCandidate,
    on,
    off,
  };
};

export default SocketContext;
